import { formatDistanceToNowStrict } from 'date-fns';
import type { VoidFunctionComponent } from 'react';

import { formatDateWithTimeZone } from '../../../helpers/date';
import TooltipBadge from '../../generic/Badge/TooltipBadge';
import Flex from '../../generic/Flex';
import type { OrgDeletionStatus } from '../../../../shared/InternalTools/orgDeletion';
import type { ListOrganizationDeletionsResponse } from '../../../../shared/types/routes/internalTools/orgDeletion';

export type OrgDeletionRow = ListOrganizationDeletionsResponse['results'][number];

type CellProps = {
  original: OrgDeletionRow;
};

type ValueCellProps = {
  value: string | null;
};

export const ORG_DELETION_STATUS_LABELS: Record<OrgDeletionStatus, string> = {
  SCHEDULED: 'Scheduled',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  FAILED: 'Failed',
};

export const OrganizationCell: VoidFunctionComponent<CellProps> = ({ original }) => (
  <Flex direction="column" align="flex-start">
    <span>{`#${original.organizationId}`}</span>
    <span>{original.organizationTitle}</span>
  </Flex>
);

/**
 * The status, with a Queued or Stuck badge when the delete hasn't started on time or hasn't finished. Nothing else
 * alerts on either, so this is where someone notices.
 */
export const StatusCell: VoidFunctionComponent<CellProps> = ({ original }) => (
  <Flex direction="column" align="flex-start" gap={4}>
    <span>{ORG_DELETION_STATUS_LABELS[original.status]}</span>
    {original.isQueued && (
      <TooltipBadge
        id={`org-deletion-${original.id}-queued`}
        tooltip={`${formatDistanceToNowStrict(new Date(original.scheduledFor))} past its scheduled time and not started.`}
        color="yellow"
      >
        Queued
      </TooltipBadge>
    )}
    {original.isStuck && (
      <TooltipBadge
        id={`org-deletion-${original.id}-stuck`}
        tooltip="Running for over 2 days. Tell Core on-call."
        color="red"
      >
        Stuck
      </TooltipBadge>
    )}
  </Flex>
);

export const DateCell: VoidFunctionComponent<ValueCellProps> = ({ value }) => (
  <span>{value ? formatDateWithTimeZone(value) : 'N/A'}</span>
);

export const TextCell: VoidFunctionComponent<ValueCellProps> = ({ value }) => (
  <span>{value ?? 'N/A'}</span>
);
